import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardCheck, ChevronRight, Trophy, SkipForward, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  userAssessmentApi,
  type UserAssessmentStartResponse,
  type AssessmentQuestionResponse,
} from '@/services/learningManagementApi';
import { gamificationApi } from '@/services/gamificationApi';

interface AssessmentSectionProps {
  userId: string;
  assessmentId: string;  // the UserAssessment ID from daily_contents or final_assessment_id
  onComplete: (score: number) => void;
  onSkip: () => void;
}

export const AssessmentSection: React.FC<AssessmentSectionProps> = ({
  userId,
  assessmentId,
  onComplete,
  onSkip,
}) => {
  const [phase, setPhase] = useState<'loading' | 'quiz' | 'submitting' | 'results'>('loading');
  const [userAssessmentId, setUserAssessmentId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<AssessmentQuestionResponse[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [score, setScore] = useState<number | null>(null);
  const [passed, setPassed] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadAssessment = useCallback(async () => {
    try {
      setPhase('loading');
      setError(null);
      const res: UserAssessmentStartResponse = await userAssessmentApi.start(assessmentId, userId);
      setUserAssessmentId(res.id);
      setQuestions(res.questions ?? []);
      if (!res.questions || res.questions.length === 0) {
        // No questions — skip assessment
        onSkip();
        return;
      }
      setPhase('quiz');
    } catch (err) {
      console.error('[AssessmentSection] Failed to load assessment:', err);
      setError('Failed to load assessment. You can skip for now.');
      setPhase('quiz');
    }
  }, [assessmentId, userId, onSkip]);

  useEffect(() => {
    loadAssessment();
  }, [loadAssessment]);

  const currentQuestion = questions[currentIdx];
  const totalQuestions = questions.length;
  const isLastQuestion = currentIdx === totalQuestions - 1;
  const hasAnswered = currentQuestion ? !!answers[currentQuestion.questionId] : false;

  const handleSelectOption = (optionId: string) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.questionId]: optionId }));
  };

  const handleNext = () => {
    if (!isLastQuestion) {
      setCurrentIdx((prev) => prev + 1);
    }
  };

  const handleSubmit = async () => {
    if (!userAssessmentId) return;
    setPhase('submitting');
    try {
      const answerList = Object.entries(answers).map(([questionId, selectedOptionId]) => ({
        questionId,
        selectedOptionId,
      }));
      const result = await userAssessmentApi.submit(userAssessmentId, answerList);
      setScore(result.percentageScore ?? 0);
      setPassed(result.passed ?? false);
      setPhase('results');

      // Record activity for gamification
      try { await gamificationApi.recordActivity(userId); } catch { /* non-blocking */ }
    } catch (err) {
      console.error('[AssessmentSection] Submit failed:', err);
      setPhase('quiz');
    }
  };

  if (phase === 'loading') {
    return (
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-violet-500/5 shadow-lg">
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-5 w-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">Loading assessment...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-violet-500/5 shadow-lg">
        <CardContent className="py-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">{error}</p>
          <Button variant="ghost" size="sm" onClick={onSkip}>
            <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (phase === 'results') {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-violet-500/5 shadow-lg">
          <CardContent className="py-8 text-center space-y-4">
            <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
              passed ? 'bg-emerald-500/15' : 'bg-amber-500/15'
            }`}>
              <Trophy className={`h-8 w-8 ${passed ? 'text-emerald-500' : 'text-amber-500'}`} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-foreground">
                {passed ? 'Great job!' : 'Nice effort!'}
              </h3>
              <p className="text-3xl font-bold mt-1">
                <span className={passed ? 'text-emerald-500' : 'text-amber-500'}>{score}%</span>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {passed
                  ? 'You demonstrated solid understanding of the material.'
                  : 'Keep practicing — every attempt strengthens your skills.'}
              </p>
            </div>
            <Button onClick={() => onComplete(score ?? 0)} className="gap-2">
              Continue <ChevronRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Quiz phase
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-violet-500/5 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15">
              <ClipboardCheck className="h-4 w-4 text-primary" />
            </div>
            Quick Knowledge Check
          </CardTitle>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Test what you learned — {totalQuestions} quick questions
            </p>
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
              {currentIdx + 1} / {totalQuestions}
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 w-full rounded-full bg-muted mt-2">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIdx + (hasAnswered ? 1 : 0)) / totalQuestions) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </CardHeader>
        <CardContent>
          <AnimatePresence mode="wait">
            {currentQuestion && (
              <motion.div
                key={currentQuestion.questionId}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="space-y-3"
              >
                <p className="text-sm font-medium text-foreground leading-relaxed">
                  {currentQuestion.questionText}
                </p>
                <div className="space-y-2">
                  {currentQuestion.answerOptions?.map((opt) => {
                    const selected = answers[currentQuestion.questionId] === opt.optionId;
                    return (
                      <button
                        key={opt.optionId}
                        type="button"
                        onClick={() => handleSelectOption(opt.optionId)}
                        className={`w-full text-left rounded-lg border-2 px-4 py-3 text-sm transition-all ${
                          selected
                            ? 'border-primary bg-primary/10 text-foreground font-medium'
                            : 'border-border bg-background/50 text-foreground/80 hover:border-primary/40 hover:bg-primary/5'
                        }`}
                      >
                        {opt.optionText}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-3 pt-4">
            {isLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={!hasAnswered || phase === 'submitting'}
                className="gap-2"
              >
                {phase === 'submitting' ? 'Submitting...' : 'Submit Answers'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!hasAnswered}
                className="gap-2"
              >
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
              <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};
